/**
 * Parent → sibling auth-status mappings for CPT multi-order save.
 * @module cptSiblingStatusMappings
 */
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import { JCodeDetails } from "../../../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../../../enums/medicalPaOrdersAuthStatus";

/** Maps a parent-order J-code status to the status used on later-DOS siblings. */
export interface CptSiblingStatusMapping {
  /** Parent payload / table status that triggers sibling DOS rules. */
  current_status: string;
  /** Status written on later-DOS sibling J-code rows. */
  sibling_status: string;
}

/**
 * Parent statuses that trigger later-DOS sibling filtering and status rewrite.
 *
 * When the CPT payload includes any `current_status` below, same/earlier-DOS
 * siblings are skipped on save and later-DOS siblings receive `sibling_status`
 * for matching J-code rows.
 */
export const CPT_SIBLING_STATUS_MAPPINGS: CptSiblingStatusMapping[] = [
  {
    current_status: MedicalPaOrdersAuthStatus.AuthByRISA,
    sibling_status: MedicalPaOrdersAuthStatus.AuthOnFile,
  },
  {
    current_status: MedicalPaOrdersAuthStatus.DenialByRISA,
    sibling_status: MedicalPaOrdersAuthStatus.ExistingDenial,
  },
  {
    current_status: MedicalPaOrdersAuthStatus.DeniedByRISA,
    sibling_status: MedicalPaOrdersAuthStatus.ExistingDenial,
  },
  {
    current_status: MedicalPaOrdersAuthStatus.DenialAfterQuery,
    sibling_status: MedicalPaOrdersAuthStatus.ExistingDenial,
  },
];

/**
 * Returns whether any J-code row uses a mapped `current_status`.
 *
 * @param jCodes - J-code rows from the CPT tables update payload
 * @param mappings - Parent → sibling status map entries
 * @returns True when at least one row matches a mapping `current_status`
 */
export const payloadHasMappedSiblingStatus = (
  jCodes: JCodeDetails[] | undefined,
  mappings: CptSiblingStatusMapping[] = CPT_SIBLING_STATUS_MAPPINGS,
): boolean => {
  const currentStatuses = new Set(
    mappings.map((mapping) => mapping.current_status),
  );

  return (jCodes ?? []).some((jCode) =>
    currentStatuses.has(jCode.auth_status ?? ""),
  );
};

/**
 * Returns whether any CPT table row uses a mapped `current_status`.
 *
 * @param tableData - Live CPT table rows from the editor
 * @param mappings - Parent → sibling status map entries
 * @returns True when at least one row's PA-required status is mapped
 */
export const cptTableHasMappedSiblingStatus = (
  tableData: CptCodesTableRowModel[],
  mappings: CptSiblingStatusMapping[] = CPT_SIBLING_STATUS_MAPPINGS,
): boolean => {
  const currentStatuses = new Set(
    mappings.map((mapping) => mapping.current_status),
  );

  return tableData.some((row) =>
    currentStatuses.has(row.paRequired?.currentStatusId ?? ""),
  );
};
